import { t } from 'i18next'
import { IAssess, IAssessData, IAssignment, IAssignmentParams, IStudentAssignment, ITableRes, StudentAssignments } from 'types'

import { baseApi } from './baseApi'

export const assignmentsAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    GET_assignments: builder.query<ITableRes<IAssignment>, IAssignmentParams>({
      query: (params) => ({
        url: '/assignments/',
        params,
      }),
      providesTags: ['assignments'],
    }),
    GET_assignmentTypes: builder.query<any, any>({
      query: (params) => ({
        url: '/',
        params,
      }),
      transformResponse: (response: any): any => {
        return {
          list: [
            { id: 1, title: 'Homework' },
            { id: 2, title: 'Test' },
            { id: 3, title: 'Final test' },
          ],
          hasMore: false,
        }
      },
    }),
    GET_assessmentTypes: builder.query<any, any>({
      query: (params) => ({
        url: '/',
        params,
      }),
      transformResponse: (response: any): any => {
        return {
          list: [
            { id: 'formative', title: 'Formative' },
            { id: 'sumative', title: 'Summative' },
          ],
          hasMore: false,
        }
      },
    }),
    // CRUD operations
    GET_assignmentById: builder.query<IAssignment, string>({
      query: (id) => {
        return {
          url: `/assignments/${id}/`,
        }
      },
      transformResponse: (response: any): any => {
        const gradingComponents = response.grading_components.map((component: any) => ({...component, importance: {id: component.importance, title: `${component.importance}`}}))
        return {
         ...response,
         status: {id: response?.status, title: `${response?.status}`},
         attachments: [],
         submission_type: {id: response?.submission_type, title: `${response?.submission_type}`},
         assessment_type: {id: response?.assessment_type, title: `${response?.assessment_type}`},
         score_type:  response?.score_type ? {id: response?.score_type, title: t(`common.${response?.score_type}`)} : null,
         importance: {id: response?.importance, title: `${response?.importance}`},
         number_of_attempts: {id: response?.number_of_attempts, title: `${response?.number_of_attempts}`},
         grading_components: gradingComponents
        }
      },
      providesTags: ['assignment'],
    }),
    POST_assignment: builder.mutation<IAssignment, IAssignment>({
      query: (body) => ({
        url: `/assignments/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['assignment', 'assignments'],
    }),
    PATCH_assignment: builder.mutation<IAssignment, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/assignments/${id}/update-due-date-and-status/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['assignment', 'assignments'],
    }),
    PUT_assignment: builder.mutation<IAssignment, { id: string; body: IAssignment }>({
      query: ({ id, body }) => ({
        url: `/assignments/${id}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['assignment', 'assignments'],
    }),
    DELETE_assignment: builder.mutation<IAssignment, string>({
      query: (id) => ({
        url: `/assignments/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['assignment', 'assignments'],
    }),
    DELETE_assignment_attachments: builder.mutation<IAssignment, { id: string; attachmentId: string }>({
      query: ({ id, attachmentId }) => ({
        url: `/assignments/${id}/materials/${attachmentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['assignment', 'assignments'],
    }),
    POST_assignment_attachments: builder.mutation<IAssignment, { id: string; formData: any }>({
      query: ({ id, formData }) => ({
        url: `/assignments/${id}/add_materials/`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['assignment', 'assignments'],
    }),
    GET_assignmentAssess: builder.query<IAssess, string>({
      query: (id) => {
        return {
          url: `/assess-assignment/${id}/`,
        }
      },
      providesTags: ['assess'],
    }),
    POST_assignmentAssess: builder.mutation<IAssess,  { id: string; data: IAssessData }>({
      query: ({ id, data }) => ({
        url: `/assess-assignment/${id}/`,
        method: 'POST',
        body: data,
      }),
      // Grading also updates the submission's assessment, KPI evaluations and Ledger events on the server
      invalidatesTags: ['assess', 'submission', 'submissions', 'kpi-evaluations', 'ledger'],
    }),
    GET_studentAssignments: builder.query<ITableRes<StudentAssignments>, IAssignmentParams>({
      query: (params) => ({
        url: '/assignments-for-student/',
        params,
      }),
      providesTags: ['student-assignments'],
    }),
    GET_studentAssignmentById: builder.query<IStudentAssignment, string>({
      query: (id) => ({
        url: `/assignments-for-student/${id}/`
      }),
      providesTags: ['student-assignment'],
    }),
  }),
})

export const {
  useGET_assignmentsQuery,
  useGET_assignmentTypesQuery,
  useGET_assessmentTypesQuery,
  useGET_assignmentByIdQuery,
  usePOST_assignmentMutation,
  usePUT_assignmentMutation,
  useDELETE_assignmentMutation,
  usePOST_assignment_attachmentsMutation,
  usePATCH_assignmentMutation,
  useDELETE_assignment_attachmentsMutation,
  useGET_assignmentAssessQuery,
  usePOST_assignmentAssessMutation,
  useGET_studentAssignmentsQuery,
  useGET_studentAssignmentByIdQuery
} = assignmentsAPI
